import { supabase } from '@/core/database/supabase';
import type { MaintenanceContract, ContractStats, ContractFilters, ContractScope, ContractSLA } from '../types/contracts.types';

export const MaintenanceContractsService = {
  /**
   * Lấy danh sách hợp đồng với bộ lọc
   */
  async getContracts(companyId: string, filters: ContractFilters = {}): Promise<{ data: MaintenanceContract[], total: number }> {
    let query = supabase
      .from('maintenance_contracts')
      .select(`
        *,
        project:projects!project_id(id, name, code)
      `, { count: 'exact' })
      .eq('company_id', companyId)
      .eq('contract_type', 'maintenance');

    // Apply Filters
    if (filters.search) {
      query = query.or(`contract_name.ilike.%${filters.search}%,contract_code.ilike.%${filters.search}%`);
    }
    if (filters.status && filters.status !== 'all') {
      query = query.eq('status_code', filters.status);
    }
    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }
    if (filters.type && filters.type !== 'all') {
      query = query.eq('contract_type', filters.type);
    }

    // Pagination
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 10;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    query = query
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as MaintenanceContract[],
      total: count || 0
    };
  },

  /**
   * Lấy chi tiết hợp đồng
   */
  async getContractDetail(id: string): Promise<MaintenanceContract | null> {
    const { data, error } = await supabase
      .from('maintenance_contracts')
      .select(`
        *,
        project:projects!project_id(id, name, code)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data as MaintenanceContract;
  },

  /**
   * Tạo hợp đồng mới
   */
  async createContract(data: Partial<MaintenanceContract>): Promise<MaintenanceContract> {
    const { data: result, error } = await supabase
      .from('maintenance_contracts')
      .insert([{ ...data, contract_type: 'maintenance' }])
      .select()
      .single();

    if (error) throw error;
    return result as MaintenanceContract;
  },

  /**
   * Cập nhật hợp đồng
   */
  async updateContract(id: string, updates: Partial<MaintenanceContract>): Promise<MaintenanceContract> {
    const { data, error } = await supabase
      .from('maintenance_contracts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as MaintenanceContract;
  },

  /**
   * Xóa mềm hợp đồng
   */
  async deleteContract(id: string): Promise<void> {
    const { error } = await supabase
      .from('maintenance_contracts')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Lấy thống kê hợp đồng
   */
  async getContractStats(companyId: string, type?: string): Promise<ContractStats> {
    let query = supabase
      .from('maintenance_contracts')
      .select('status_code, contract_value, end_date, contract_type')
      .eq('company_id', companyId)
      .eq('contract_type', 'maintenance');

    if (type && type !== 'all') {
      query = query.eq('contract_type', type);
    }

    const { data, error } = await query;

    if (error) throw error;

    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);

    const stats: ContractStats = {
      total: data.length,
      active: data.filter(c => c.status_code === 'active').length,
      nearExpiry: data.filter(c => {
        if (!c.end_date) return false;
        const expiryDate = new Date(c.end_date);
        return expiryDate > now && expiryDate <= thirtyDaysFromNow;
      }).length,
      totalValue: data.reduce((sum, c) => sum + (c.contract_value || 0), 0),
    };

    return stats;
  },

  /**
   * Lấy phạm vi hợp đồng
   */
  async getContractScopes(contractId: string): Promise<ContractScope[]> {
    const { data, error } = await supabase
      .from('maintenance_contract_scopes')
      .select('*')
      .eq('contract_id', contractId);

    if (error) throw error;
    return (data || []) as ContractScope[];
  },

  /**
   * Lấy SLA hợp đồng
   */
  async getContractSLA(contractId: string): Promise<ContractSLA[]> {
    const { data, error } = await supabase
      .from('maintenance_contract_sla')
      .select('*')
      .eq('contract_id', contractId);

    if (error) throw error;
    return (data || []) as ContractSLA[];
  },

  /**
   * Lấy danh sách khu vực thuộc hợp đồng
   */
  async getContractZones(contractId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('maintenance_contract_zones')
      .select(`
        *,
        zone:project_zones(*)
      `)
      .eq('contract_id', contractId);

    if (error) throw error;
    return data || [];
  },

  /**
   * Gán các khu vực vào hợp đồng
   */
  async assignZonesToContract(contractId: string, companyId: string, zoneIds: string[]): Promise<void> {
    const payloads = zoneIds.map(zoneId => ({
      contract_id: contractId,
      zone_id: zoneId,
      company_id: companyId,
      area_m2: 0, // Default or fetch from zone
      difficulty_level: 'medium',
      visit_frequency: 'weekly'
    }));

    const { error } = await supabase
      .from('maintenance_contract_zones')
      .insert(payloads);

    if (error) throw error;
  },

  /**
   * Gỡ khu vực khỏi hợp đồng
   */
  async removeZoneFromContract(assignmentId: string): Promise<void> {
    const { error } = await supabase
      .from('maintenance_contract_zones')
      .delete()
      .eq('id', assignmentId);

    if (error) throw error;
  }
};
